import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CircularProgress from "@material-ui/core/CircularProgress";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import Chip from "@material-ui/core/Chip";
import Box from "@material-ui/core/Box";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(theme => ({
  loadingBox: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(3),
  },
  tagsWrapper: {
    display: "flex",
    flexWrap: "wrap",
    gap: 6,
    marginBottom: theme.spacing(2),
  },
  previewBox: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.type === "dark" ? "#2b2b2b" : "#f5f5f5",
    borderRadius: 4,
    whiteSpace: "pre-wrap",
  },
}));

const PREVIEW_VALUES = {
  "{nome}": "Maria Silva",
  "{numero}": "(45) 99999-9999",
  "{palavra}": "imposto de renda",
  "{regra}": "Imposto de Renda",
  "{mensagem}": "Oi, queria saber sobre o imposto de renda desse ano",
};

const buildPreview = template => {
  let preview = template || "";
  Object.entries(PREVIEW_VALUES).forEach(([tag, value]) => {
    preview = preview.split(tag).join(value);
  });
  return preview;
};

// Modal pra configurar o texto da mensagem de notificação enviada aos
// atendentes, com tags clicáveis que inserem {tag} no ponto do cursor.
const NotificationMessageTemplateModal = ({ open, onClose }) => {
  const classes = useStyles();
  const textFieldRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [template, setTemplate] = useState("");
  const [defaultTemplate, setDefaultTemplate] = useState("");
  const [tags, setTags] = useState([]);

  const fetchTemplate = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/notification-rules/message-template");
      setTemplate(data.template);
      setDefaultTemplate(data.defaultTemplate);
      setTags(data.tags || []);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) fetchTemplate();
  }, [open]);

  const handleInsertTag = tag => {
    const input = textFieldRef.current;
    if (!input) {
      setTemplate(prev => `${prev}${tag}`);
      return;
    }
    const start = input.selectionStart ?? template.length;
    const end = input.selectionEnd ?? template.length;
    const newValue = `${template.slice(0, start)}${tag}${template.slice(end)}`;
    setTemplate(newValue);
    // Depois de re-renderizar, devolve o foco e posiciona o cursor após a tag inserida.
    setTimeout(() => {
      input.focus();
      const cursorPos = start + tag.length;
      input.setSelectionRange(cursorPos, cursorPos);
    }, 0);
  };

  const handleRestoreDefault = () => {
    setTemplate(defaultTemplate);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put("/notification-rules/message-template", { template });
      toast.success("Mensagem de notificação salva com sucesso!");
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Configurar mensagem de notificação</DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box className={classes.loadingBox}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Essa é a mensagem enviada ao WhatsApp do atendente quando uma regra é
              disparada. Clique em uma tag para inserir no texto.
            </Typography>
            <Box className={classes.tagsWrapper}>
              {tags.map(({ tag, label }) => (
                <Chip
                  key={tag}
                  label={`${tag} — ${label}`}
                  size="small"
                  color="primary"
                  variant="outlined"
                  onClick={() => handleInsertTag(tag)}
                  clickable
                />
              ))}
            </Box>
            <TextField
              inputRef={textFieldRef}
              value={template}
              onChange={e => setTemplate(e.target.value)}
              multiline
              minRows={5}
              fullWidth
              variant="outlined"
            />
            <Typography variant="caption" color="textSecondary">
              Pré-visualização (com dados de exemplo):
            </Typography>
            <Box className={classes.previewBox}>
              <Typography variant="body2">{buildPreview(template)}</Typography>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" variant="outlined">
          Fechar
        </Button>
        {!loading && (
          <>
            <Button onClick={handleRestoreDefault} color="secondary">
              Restaurar padrão
            </Button>
            <Button
              onClick={handleSave}
              color="primary"
              variant="contained"
              disabled={saving || !template.trim()}
            >
              {saving ? <CircularProgress size={20} /> : "Salvar"}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default NotificationMessageTemplateModal;
